import { z } from 'zod';
import { OptionsSchema as BaseOptionsSchema, AuiTokenSchema, TextPropsSchema } from '../../WidgetVariant';

const QuickInputOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  auiIcon: z.string().optional(),
  href: z.string().url().optional(),
});

export type DisplayModes = 'NORMAL' | 'FULLSCREEN';

const LandingPanelTitleSchema = z.union([z.string(), TextPropsSchema]);

const EmbeddedBaseSchema = z.object({
  dualMode: z.boolean().optional(),
  callbackConfirmationImg: z
    .object({
      alt: z.string().optional(),
      src: z.string(),
    })
    .optional(),
  chatPanelTextInputLabel: z.string().optional(),
  experimental: z
    .object({
      mlContextualDialog: z
        .object({
          enabled: z.boolean().optional(),
          message: z.string().optional(),
          options: z.array(QuickInputOptionSchema).optional(),
        })
        .optional(),
    })
    .optional(),
  sidePanelPlaceholderImage: z
    .object({
      alt: z.string().optional(),
      src: z.string(),
    })
    .optional(),
  sidePanel: z
    .object({
      placeholderImage: z.object({
        alt: z.string().optional(),
        src: z.string(),
      }),
      imageObjectFit: z.enum(['cover', 'contain']).default('cover'),
      backgroundColor: z.string().optional(),
    })
    .optional(),
  landingPanel: z
    .object({
      logo: z
        .object({
          alt: z.string().optional(),
          src: z.string(),
          lottieUrl: z.string().optional(),
        })
        .optional(),
      intents: z.array(QuickInputOptionSchema).optional(),
      textInputLabel: z.string().optional(),
      hideInput: z.boolean().optional(),
      title: LandingPanelTitleSchema.optional(),
    })
    .optional(),
  supportWindowHeader: z
    .object({
      text: z.string().optional(),
      textAlign: z.enum(['left', 'center', 'right']).optional(),
    })
    .optional(),
  fullScreenMode: z.boolean().optional(),
  isJoyUI: z.boolean().optional(),
});

const EmbeddedSchema = EmbeddedBaseSchema.merge(AuiTokenSchema);

export const OptionsSchema = BaseOptionsSchema.extend({
  embedded: EmbeddedSchema.transform(({ sidePanelPlaceholderImage, sidePanel, ...rest }) => {
    let updatedSidePanel = sidePanel;

    if (sidePanelPlaceholderImage) {
      updatedSidePanel = {
        ...sidePanel,
        imageObjectFit: sidePanel?.imageObjectFit ?? 'cover',
        placeholderImage: sidePanelPlaceholderImage,
      };
    }

    return {
      ...rest,
      sidePanelPlaceholderImage,
      sidePanel: updatedSidePanel,
    };
  }),
});

export const PartialOptionsSchema = BaseOptionsSchema.extend({
  embedded: EmbeddedSchema.partial(),
}).partial();

export type Options = z.infer<typeof OptionsSchema>;
export type PartialOptions = z.infer<typeof PartialOptionsSchema>;
